<template>
  <v-container class="providers-container">
    <v-layout row wrap class="px-3">
      <v-flex xs12 sm9 :class="{'pb-0': isMobile}">
        <v-tooltip :disabled="!showTooltip" top>
          <v-autocomplete
            slot="activator"
            v-model="newProvider"
            dense
            class="ma-0"
            label="Add New Provider"
            placeholder="Enter Provider Name"
            :return-object="true"
            :items="providersArray"
            :loading="providerSearchLoading"
            :error-messages="providerSearchError"
            :search-input.sync="providerSearchInput"
            :no-filter="true"
            @focus="clearNewProviderAutoComplete"
            :clearable="true">
            <template slot="item" slot-scope="{item, tile}">
              {{item.firstName}} {{item.lastName}}
              <span v-if="item.credentialType">/</span>
              <span v-if="isMobile">{{item.credentialType}}</span>
              <span v-else>{{item.credentialName}}</span>
            </template>
            <template slot="selection" slot-scope="{item, selected}">
              {{item.firstName}} {{item.lastName}}
              <span v-if="item.credentialType">/</span>
              <span v-if="isMobile">{{item.credentialType}}</span>
              <span v-else>{{item.credentialName}}</span>
            </template>
            <!--message that displays when search matches nothing-->
            <template slot="no-data">
              <v-list>
                <!--message when no input is entered-->
                <v-list-tile v-if="!providerSearchInput">
                  Please type a Provider.
                </v-list-tile>
                <!--message when no provider matches text-->
                <v-list-tile v-if="providerSearchInput && !providerSearchLoading">
                  No Results
                </v-list-tile>
              </v-list>
            </template>
          </v-autocomplete>
          <span>Select a provider to add</span>
        </v-tooltip>
      </v-flex>
      <v-flex xs12 sm3 :class="{'pt-0': isMobile}">
        <v-tooltip :disabled="!showTooltip" top :class="{ 'float-right': !isMobile }" style="margin-left: -8px;">
          <v-btn
            slot="activator"
            @click="addNewProviderByStudent(null)"
            class="add-new-provider-button"
            :class="$mq"
            color="secondary"
            round>
              <span>
                <v-icon small class="icon-padding">mdi-account-plus</v-icon>
                <small>Add Provider</small>
              </span>
          </v-btn>
          <span>Add provider to student team and service</span>
        </v-tooltip>
      </v-flex>
    </v-layout>
    <v-layout row wrap class="px-3" v-if="service.providers.length">
      <v-flex xs12 class="py-0">
        <span class="subheading font-bold">Manage Providers</span>
      </v-flex>
    </v-layout>
    <v-layout row wrap class="px-4 py-2" v-if="service.providers.length">
      <v-flex xs12 v-for="(provider, index) in service.providers" :key="index" class="py-1 my-1 provider-row">
        <v-layout row wrap fill-height align-center>
          <v-flex xs9 class="pa-0">
            {{ provider.providerFirstName + ' ' + provider.providerLastName }}
            <span v-if="isMobile && provider.credentialType">
              / {{ provider.credentialType }}
            </span>
            <span v-if="!isMobile && provider.credentialName">
              / {{ provider.credentialName }}
            </span>
          </v-flex>
          <v-flex xs3 class="pa-0">
            <span
              v-if="provider.addType"
              @click="addNewProviderByStudent(provider.providerUserId)"
              class="float-right">
              <span v-if="isMobile">
                <span v-if="provider.addType === 'TEAM'">
                  <v-icon class="secondary--text">
                    mdi-lock-plus
                  </v-icon>
                </span>
              </span>
              <span v-else>
                <v-btn
                  class="add-button"
                  color="secondary"
                  small round>
                  <span v-if="provider.addType === 'TEAM'">
                    <v-tooltip :disabled="!showTooltip" top>
                      <span slot="activator">
                        <v-icon small class="icon-padding">
                          mdi-lock-plus
                        </v-icon>
                        <small>Add Team</small>
                      </span>
                      <span>Add provider to student team</span>
                    </v-tooltip>
                  </span>
                </v-btn>
              </span>
            </span>
            <span v-if="provider.removeType"
                  @click="removeProviderByStudent(provider.providerId)"
                  class="float-right">
              <span v-if="isMobile">
                <span v-if="provider.removeType === 'TEAM'">
                  <v-icon class="error--text">
                    mdi-lock-open
                  </v-icon>
                </span>
                <span v-if="provider.removeType === 'PROVIDER'">
                  <v-icon class="error--text">
                    mdi-account-remove
                  </v-icon>
                </span>
              </span>
              <span v-else>
                <v-btn
                  class="remove-button"
                  small round>
                  <span v-if="provider.removeType === 'TEAM'">
                    <v-tooltip :disabled="!showTooltip" top>
                      <span slot="activator">
                        <v-icon small style="padding-right: 10px">
                          mdi-lock-open
                        </v-icon>
                        <small>Remove From Team</small>
                      </span>
                      <span>Remove provider from student team</span>
                    </v-tooltip>
                  </span>
                  <span v-if="provider.removeType === 'PROVIDER'">
                    <v-tooltip :disabled="!showTooltip" top>
                      <span slot="activator">
                        <v-icon small style="padding-right: 10px;">
                         mdi-account-remove
                        </v-icon>
                        <small>Remove Provider</small>
                      </span>
                      <span>Remove provider from student team and service</span>
                    </v-tooltip>
                  </span>
                </v-btn>
              </span>
            </span>
            <span v-if="!provider.removeType && !provider.addType" class="float-right">
                <span v-if="!isMobile">
                  <v-btn class="add-button small round" style="visibility: hidden !important; margin: 0;"></v-btn>
                </span>
                <span v-else>
                  <v-icon style="visibility: hidden !important;">mdi-lock-plus</v-icon>
                </span>
              </span>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import api from '@/utils/api';
import * as Constants from '@/constants';

export default {
  name: 'ProviderAutocomplete',
  props: ['service', 'showTooltip'],
  data() {
    return {
      providerSearchError: '',
      providerSearchInput: null,
      providerSearchLoading: false,
      providersArray: [],
      newProvider: ''
    };
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    isTablet() {
      return this.$mq === 'tablet';
    },
    isDesktop() {
      return this.$mq === 'laptop' || this.$mq === 'desktop';
    }
  },
  methods: {
    addNewProviderByStudent(pId) {
      if (pId || this.newProvider.userId) {
        const body = {
          serviceId: this.service.serviceId,
          providerUserId: pId || this.newProvider.userId
        };
        this.newProvider = null;
        api.post(Constants.ADD_PROVIDER_TO_STUDENT_SERVICE_AND_TEAM_URL(), body)
          .then(() => {
            // clear the array
            this.providersArray = [];
            this.$emit('update-services');
          });
      }
    },
    removeProviderByStudent(id) {
      this.showLoading = true;
      this.errorMsg = null;
      api.delete(Constants.REMOVE_PROVIDER_FROM_STUDENT_SERVICE_AND_TEAM_URL(id))
        .then(() => {
          this.$emit('update-services');
        });
    },
    // clears the dropdown values of the autocomplete when focusing on the field
    clearNewProviderAutoComplete() {
      if (!this.newProvider) {
        this.providersArray = [];
      }
    }
  },
  watch: {
    // fires the api call to get providers list
    providerSearchInput() {
      // show loading
      this.providerSearchLoading = true;
      // clear timeout if function is called before setTimeout fires
      clearTimeout(this.timeout);
      // make sure the user types something before firing request
      if (this.providerSearchInput) {
        // fire the request after a delay
        this.timeout = setTimeout(() => {
          api.get(Constants.ADD_NEW_PROVIDER_TO_SERVICE_URL(this.service.serviceId, this.providerSearchInput))
            .then((res) => {
              this.providersArray = res.data.data;
            })
            .catch((error) => {
              this.providerSearchError = error.message;
            })
            .finally(() => {
              // stop loading
              this.providerSearchLoading = false;
            });
        }, 300);
      } else {
        this.providerSearchLoading = false;
      }
    },
    // watcher for autocomplete model
    newProvider(newVal, oldVal) {
      // if newVal is undefined and there was an oldVal, clear the providers array
      if (!newVal && !!oldVal) this.providersArray = [];
    }
  }
};
</script>

<style lang="scss" scoped>
  .providers-container {
    padding: 20px 0;
    .add-new-provider-button {
      width: 150px;
      margin-top: 12px;
      &.mobile {
        width: 100%;
        margin-top: 0;
      }
    }
    .add-button {
      width: 150px;
    }
    .remove-button {
      width: 150px;
      color: var(--v-error-base);
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.12);
    }
    .layout {
      width: 100%;
      margin: 0 !important;
    }
    .provider-row {
      border-radius: 8px;
      box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
      background-color: #DEE8F8;
      color: var(--v-secondary-darken2);
      font-weight: 500;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/scheduler/add-new-provider-autocomplete.vue